"use client";

import { useCallback, useMemo, useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  QUESTIONNAIRE_FIELDS as F,
  formatQuestionnaire,
  type QuestionnaireLabels,
  type QuestionnaireSource,
  questionnaireIsReady,
} from "@/lib/tool-kit/special-sources/questionnaire-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const QUESTIONNAIRE_CITATION_GENERATOR_STRINGS = {
  formHeading: "Test or scale details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  sourceLabel: "What are you citing?",
  sourceRecord: "A PsycTESTS record",
  sourceManual: "A test manual",
  sourceArticle: "A scale published in an article",
  sourceUnpublished: "An unpublished questionnaire",
  introRecord:
    "Cite a test database record when you use what the record adds — its description, its items or its psychometric details.",
  introManual:
    "A published test is cited by its manual, the book that documents it. Cite the manual even when you only gave the test.",
  introArticle:
    "Many scales were first published in a journal article. Cite that article, and name the scale in your sentence.",
  introUnpublished:
    "A questionnaire written for a study that was never published: give its author, year, title and institution.",
  authorsLabel: "Authors, one per line",
  authorsPlaceholder: "Kurt Kroenke\nRobert L. Spitzer\nJanet B. W. Williams",
  authorsHint:
    "First name then surname, in the order the source lists them. Full first names help MLA and Chicago.",
  yearLabel: "Year",
  yearPlaceholder: "2001",
  titleRecordLabel: "Test name, as the record gives it",
  titleRecordPlaceholder:
    "Situated Goals Questionnaire for University Students (SGQ-U, CMS-U)",
  titleManualLabel: "Title of the manual",
  titleManualPlaceholder: "Manual for the Beck Depression Inventory-II",
  titleArticleLabel: "Title of the article",
  titleArticlePlaceholder:
    "The PHQ-9: Validity of a brief depression severity measure",
  titleArticleHint:
    "In sentence case, as APA wants it. The tool capitalizes it for MLA and Chicago.",
  titleUnpublishedLabel: "Title of the questionnaire",
  titleUnpublishedPlaceholder: "Campus Belonging Questionnaire",
  databaseLabel: "Database",
  databasePlaceholder: "PsycTESTS",
  publisherLabel: "Publisher",
  publisherPlaceholder: "Psychological Corporation",
  journalLabel: "Journal",
  journalPlaceholder: "Journal of General Internal Medicine",
  volumeLabel: "Volume",
  volumePlaceholder: "16",
  issueLabel: "Issue",
  issuePlaceholder: "9",
  pagesLabel: "Pages",
  pagesPlaceholder: "606–613",
  institutionLabel: "Department and institution",
  institutionPlaceholder: "Department of Psychology, Example State University",
  descriptionLabel: "Description (optional)",
  descriptionPlaceholder: "Unpublished questionnaire",
  doiLabel: "DOI or URL",
  doiPlaceholder: "10.1037/t66267-000",
  doiArticlePlaceholder: "10.1046/j.1525-1497.2001.016009606.x",
  testNameLabel: "Name of the test or scale (optional)",
  testNamePlaceholder: "Patient Health Questionnaire-9",
  testNameHint:
    "Gives you the sentence form for naming the measure, with its citation.",
  testAbbreviationLabel: "Its abbreviation (optional)",
  testAbbreviationPlaceholder: "PHQ-9",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  namingTest: "Naming the measure in your text",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  bibliography: "Bibliography",
  apaNote:
    "APA cites a test by its supporting literature — the manual or the article it was published in — and a database record when you use the record's own information. Capitalize a test's name in your text; don't italicize it.",
  mlaNote:
    "MLA has no special format for tests: the source is cited by its type — a book, an article, a work in a database.",
  chicagoNote:
    "Chicago has no special format for tests either. The note and bibliography follow its book and article patterns.",
  exampleRecord: {
    authors:
      "Jesús Alonso-Tapia\nCarmen Nieto\nEnrique Merino-Tejedor\nJuan A. Huertas\nMiguel Ruiz",
    year: "2018",
    title:
      "Situated Goals Questionnaire for University Students (SGQ-U, CMS-U)",
    database: "PsycTESTS",
    doi: "10.1037/t66267-000",
  },
  exampleManual: {
    authors: "Aaron T. Beck\nRobert A. Steer\nGregory K. Brown",
    year: "1996",
    title: "Manual for the Beck Depression Inventory-II",
    publisher: "Psychological Corporation",
    testName: "Beck Depression Inventory-II",
    testAbbreviation: "BDI-II",
  },
  exampleArticle: {
    authors: "Kurt Kroenke\nRobert L. Spitzer\nJanet B. W. Williams",
    year: "2001",
    title: "The PHQ-9: Validity of a brief depression severity measure",
    journal: "Journal of General Internal Medicine",
    volume: "16",
    issue: "9",
    pages: "606–613",
    doi: "10.1046/j.1525-1497.2001.016009606.x",
    testName: "Patient Health Questionnaire-9",
    testAbbreviation: "PHQ-9",
  },
  exampleUnpublished: {
    authors: "Jordan Lee",
    year: "2025",
    title: "Campus Belonging Questionnaire",
    institution: "Department of Psychology, Example State University",
  },
} as const;

type Strings = typeof QUESTIONNAIRE_CITATION_GENERATOR_STRINGS;

/** The title field's label and placeholder for each kind of source. */
function titleWording(
  strings: Strings,
  source: QuestionnaireSource
): [string, string] {
  if (source === "record") {
    return [strings.titleRecordLabel, strings.titleRecordPlaceholder];
  }
  if (source === "manual") {
    return [strings.titleManualLabel, strings.titleManualPlaceholder];
  }
  if (source === "article") {
    return [strings.titleArticleLabel, strings.titleArticlePlaceholder];
  }
  return [strings.titleUnpublishedLabel, strings.titleUnpublishedPlaceholder];
}

function sourceFields(
  strings: Strings,
  source: QuestionnaireSource
): SourceField[] {
  if (source === "record") {
    return [
      {
        id: F.database,
        kind: "text",
        label: strings.databaseLabel,
        placeholder: strings.databasePlaceholder,
      },
      {
        id: F.doi,
        kind: "text",
        label: strings.doiLabel,
        placeholder: strings.doiPlaceholder,
      },
    ];
  }
  if (source === "manual") {
    return [
      {
        id: F.publisher,
        kind: "text",
        label: strings.publisherLabel,
        placeholder: strings.publisherPlaceholder,
      },
      { id: F.doi, kind: "text", label: strings.doiLabel },
    ];
  }
  if (source === "article") {
    return [
      {
        id: F.journal,
        kind: "text",
        label: strings.journalLabel,
        placeholder: strings.journalPlaceholder,
        wide: true,
      },
      {
        id: F.volume,
        kind: "text",
        label: strings.volumeLabel,
        placeholder: strings.volumePlaceholder,
      },
      {
        id: F.issue,
        kind: "text",
        label: strings.issueLabel,
        placeholder: strings.issuePlaceholder,
      },
      {
        id: F.pages,
        kind: "text",
        label: strings.pagesLabel,
        placeholder: strings.pagesPlaceholder,
      },
      {
        id: F.doi,
        kind: "text",
        label: strings.doiLabel,
        placeholder: strings.doiArticlePlaceholder,
      },
    ];
  }
  return [
    {
      id: F.institution,
      kind: "text",
      label: strings.institutionLabel,
      placeholder: strings.institutionPlaceholder,
      wide: true,
    },
    {
      id: F.description,
      kind: "text",
      label: strings.descriptionLabel,
      placeholder: strings.descriptionPlaceholder,
    },
  ];
}

function buildFields(
  strings: Strings,
  source: QuestionnaireSource
): SourceField[] {
  const [titleLabel, titlePlaceholder] = titleWording(strings, source);
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.title,
      kind: "text",
      label: titleLabel,
      placeholder: titlePlaceholder,
      hint: source === "article" ? strings.titleArticleHint : undefined,
      wide: true,
    },
    ...sourceFields(strings, source),
    {
      id: F.testName,
      kind: "text",
      label: strings.testNameLabel,
      placeholder: strings.testNamePlaceholder,
      hint: strings.testNameHint,
    },
    {
      id: F.testAbbreviation,
      kind: "text",
      label: strings.testAbbreviationLabel,
      placeholder: strings.testAbbreviationPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function questionnaireLabels(strings: Strings): QuestionnaireLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    namingTest: strings.namingTest,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

const EXAMPLES = {
  record: "exampleRecord",
  manual: "exampleManual",
  article: "exampleArticle",
  unpublished: "exampleUnpublished",
} as const;

const INTROS = {
  record: "introRecord",
  manual: "introManual",
  article: "introArticle",
  unpublished: "introUnpublished",
} as const;

export default function QuestionnaireCitationGeneratorWidget() {
  const strings = useToolUi(QUESTIONNAIRE_CITATION_GENERATOR_STRINGS);
  const [source, setSource] = useState<QuestionnaireSource>("record");
  const fields = useMemo(() => buildFields(strings, source), [strings, source]);
  const labels = useMemo(() => questionnaireLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatQuestionnaire(values, source, labels),
    [source, labels]
  );

  return (
    <SourceCitationBuilder
      example={strings[EXAMPLES[source]]}
      fields={fields}
      format={format}
      intro={
        <div className="space-y-3">
          <PillGroup
            label={strings.sourceLabel}
            name="questionnaire-citation-source"
            onChange={setSource}
            options={[
              { value: "record", label: strings.sourceRecord },
              { value: "manual", label: strings.sourceManual },
              { value: "article", label: strings.sourceArticle },
              { value: "unpublished", label: strings.sourceUnpublished },
            ]}
            value={source}
          />
          <Notice>{strings[INTROS[source]]}</Notice>
        </div>
      }
      isReady={questionnaireIsReady}
      strings={strings}
    />
  );
}
